import React from 'react';
import Button from '../Button';
import { PreviewIcon } from '../icons';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../StandardTable';
import { fmtMoney, fmtDate } from '../../utils/campaignFinanceUtils';
import StatCard, { StatCardRow } from '../StatCard';
import CommitteeLink from '../committee/CommitteeLink';

interface ContributorPatternProps {
  contributor: string;
  gifts: Array<{
    id: string;
    date: string;
    recipient: string;
    /** TRACER id of the recipient committee — the name opens the committee card. */
    recipient_co_id?: string | null;
    recipient_party?: string | null;
    amount: number;
    type?: string;
    current?: boolean;
  }>;
  currentContributionId?: string;
  onViewRecord?: (id: string) => void;
}

const ContributorPattern: React.FC<ContributorPatternProps> = ({ contributor, gifts, currentContributionId, onViewRecord }) => {
  if (gifts.length === 0) return null;

  const sorted = [...gifts].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const total = gifts.reduce((s, g) => s + (g.amount || 0), 0);
  const uniqueRecipients = new Set(gifts.map(g => g.recipient)).size;
  const maxAmt = Math.max(...gifts.map(g => g.amount || 0));

  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-5 py-4 border-b border-border-light dark:border-border-dark">
        <div className="text-xs font-semibold text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider mb-1">
          Contributor Pattern
        </div>
        <div className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">{contributor}</div>
      </div>

      {/* Metrics strip */}
      <div className="px-5 py-4 border-b border-border-light dark:border-border-dark">
        <StatCardRow minTileWidth="11rem">
          <StatCard label="Total Given" value={fmtMoney(total, { compact: true })} variant="success" />
          <StatCard label="Recipients" value={uniqueRecipients} />
          <StatCard label="Gifts" value={gifts.length} />
        </StatCardRow>
      </div>

      {/* Gifts table */}
      <StandardTable>
        <StandardTableHeader>
          <tr>
            <StandardTableHeaderCell columnType="nowrap">Date</StandardTableHeaderCell>
            <StandardTableHeaderCell>Recipient</StandardTableHeaderCell>
            <StandardTableHeaderCell align="right">Amount</StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="actions">Actions</StandardTableHeaderCell>
          </tr>
        </StandardTableHeader>
        <StandardTableBody>
          {sorted.map(g => {
            const isCurrent = g.current || g.id === currentContributionId;
            return (
              <StandardTableRow
                key={g.id}
                selected={isCurrent}
                hoverable={!isCurrent}
              >
                <StandardTableCell className="tabular-nums whitespace-nowrap">
                  {fmtDate(g.date)}
                </StandardTableCell>
                <StandardTableCell>
                  <div className="flex items-center gap-2">
                    {g.recipient_party && (
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        g.recipient_party.toLowerCase().includes('democrat') ? 'bg-party-democrat' :
                        g.recipient_party.toLowerCase().includes('republican') ? 'bg-party-republican' :
                        'bg-text-tertiary'
                      }`} />
                    )}
                    {g.recipient && g.recipient !== 'Unknown' ? (
                      <CommitteeLink coId={g.recipient_co_id} name={g.recipient} className="text-text-primary dark:text-text-dark-primary hover:text-primary-700 dark:hover:text-primary-dark-400" />
                    ) : (
                      <span className="text-text-primary dark:text-text-dark-primary">{g.recipient}</span>
                    )}
                  </div>
                </StandardTableCell>
                <StandardTableCell align="right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1 bg-background-tertiary dark:bg-background-dark-tertiary rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary-800 dark:bg-primary-dark-300"
                        style={{ width: `${maxAmt > 0 ? ((g.amount || 0) / maxAmt) * 100 : 0}%` }}
                      />
                    </div>
                    <span className={`tabular-nums ${isCurrent ? 'font-bold text-primary-800 dark:text-primary-dark-400' : 'font-medium text-text-primary dark:text-text-dark-primary'}`}>
                      {fmtMoney(g.amount || 0)}
                    </span>
                  </div>
                </StandardTableCell>
                <StandardTableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={PreviewIcon}
                    title="View record"
                    ariaLabel="View record"
                    onClick={() => onViewRecord?.(g.id)}
                  />
                </StandardTableCell>
              </StandardTableRow>
            );
          })}
        </StandardTableBody>
      </StandardTable>
    </div>
  );
};

export default ContributorPattern;
